import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Image
} from 'react-bootstrap';
import { FaSave, FaUpload, FaUserMd } from 'react-icons/fa';

const ClinicSettings = ({ clinic, setClinic }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contactNumber: '',
    email: '',
    timings: {
      open: '',
      close: ''
    },
    workingDays: [],
    patientIdPrefix: '25-HWC-',
    patientIdStart: 1
  });

  const [doctors, setDoctors] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const workingDaysOptions = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  useEffect(() => {
    // Load initial data
    if (clinic) {
      setFormData({
        name: clinic.name || '',
        address: clinic.address || '',
        contactNumber: clinic.contactNumber || '',
        email: clinic.email || '',
        timings: {
          open: clinic.timings?.open || '09:00',
          close: clinic.timings?.close || '20:00'
        },
        workingDays: clinic.workingDays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        patientIdPrefix: clinic.patientIdPrefix || '25-HWC-',
        patientIdStart: clinic.patientIdStart || 1
      });

      setLogoPreview(clinic.logo);
      
      // Process doctors array from clinic data
      console.log("Processing clinic doctors:", clinic.doctors);
      
      // Handle both clinic.doctors as Array or as JSON object with Doctors
      let doctorIds = [];
      
      if (clinic.doctors) {
        // Case 1: clinic.doctors is an array of IDs
        if (Array.isArray(clinic.doctors)) {
          doctorIds = clinic.doctors
            .filter(doc => doc !== null && doc !== undefined)
            .map(doc => {
              if (typeof doc === 'object' && doc) {
                return doc._id || doc.id;
              }
              return doc;
            });
        }
        // Case 2: clinic.doctors is populated with Doctor objects
        else if (typeof clinic.doctors === 'object' && clinic.doctors !== null) {
          // Handle if it's a Sequelize result with Doctors association
          if (Array.isArray(clinic.doctors.Doctors)) {
            doctorIds = clinic.doctors.Doctors
              .filter(doc => doc !== null && doc !== undefined)
              .map(doc => doc.id || doc._id);
          }
        }
      }
      
      console.log("Setting selected doctors to:", doctorIds);
      setSelectedDoctors(doctorIds.filter(id => id !== null && id !== undefined));
    }

    // Fetch doctors
    const fetchDoctors = async () => {
      try {
        const res = await axios.get('/api/doctors.php');
        setDoctors(res.data);
        console.log("Fetched doctors:", res.data);
      } catch (err) {
        console.error('Failed to fetch doctors', err);
      }
    };

    fetchDoctors();
  }, [clinic]);

  const handleInputChange = e => {
    if (e.target.name.includes('.')) {
      const [parent, child] = e.target.name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: e.target.value
        }
      });
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    }
  };

  const handleWorkingDaysChange = day => {
    if (formData.workingDays.includes(day)) {
      setFormData({
        ...formData,
        workingDays: formData.workingDays.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        workingDays: [...formData.workingDays, day]
      });
    }
  };

  const handleDoctorSelection = doctorId => {
    // Convert the doctor ID to string for consistency
    const doctorIdStr = String(doctorId);
    console.log(`Doctor selection toggled: ${doctorIdStr}`);
    console.log(`Current selected doctors: ${JSON.stringify(selectedDoctors)}`);
    
    // Check if this doctor is already selected by comparing string representations
    const isSelected = selectedDoctors.some(id => String(id) === doctorIdStr);
    
    if (isSelected) {
      // If already selected, remove it
      console.log(`Removing doctor ${doctorIdStr} from selection`);
      setSelectedDoctors(selectedDoctors.filter(id => String(id) !== doctorIdStr));
    } else {
      // If not selected and we have less than 3 doctors selected, add it
      if (selectedDoctors.length < 3) {
        console.log(`Adding doctor ${doctorIdStr} to selection`);
        setSelectedDoctors([...selectedDoctors, doctorId]);
      } else {
        console.log(`Cannot add more than 3 doctors. Current selection: ${JSON.stringify(selectedDoctors)}`);
        alert('You can select a maximum of 3 doctors for reports');
      }
    }
  };

  const handleLogoChange = e => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);

      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('logo', logoFile);

    try {
      const res = await axios.post('/api/uploads/logo.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Refresh clinic data from server
      const clinicRes = await axios.get('/api/clinic.php');
      setClinic(clinicRes.data);
      setLogoPreview(clinicRes.data.logo);
      
      // Clear file input
      setLogoFile(null);
      document.querySelector('input[type="file"]').value = '';
      
      setSuccess('Logo uploaded successfully');
      setLoading(false);
    } catch (err) {
      setError('Failed to upload logo: ' + (err.response?.data.error || err.message));
      setLoading(false);
    }
  };

  const handleResetIdCounter = async () => {
    if (!window.confirm('Are you sure you want to reset the patient ID counter? New patients will start from 001 again.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    // Filter out any null or undefined values from selectedDoctors
    const validDoctors = selectedDoctors.filter(id => id !== null && id !== undefined);
    
    try {
      const res = await axios.put('/api/clinic.php', {
        ...formData,
        patientIdCounter: 1,
        doctors: validDoctors
      });
      
      setClinic(res.data);
      setFormData(prev => ({
        ...prev,
        patientIdStart: 1
      }));
      setSuccess('Patient ID counter has been reset successfully!');
      setLoading(false);
    } catch (err) {
      setError('Failed to reset counter: ' + (err.response?.data.msg || err.message));
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Ensure doctor IDs are properly formatted for the server
    const validDoctors = selectedDoctors
      .filter(id => id !== null && id !== undefined)
      .map(id => {
        // Convert to number if it's a numeric string
        const numericId = parseInt(id, 10);
        return !isNaN(numericId) ? numericId : id;
      });
      
    console.log("Saving clinic with selected doctors:", validDoctors);

    try {
      const res = await axios.put('/api/clinic.php', {
        ...formData,
        doctors: validDoctors
      });

      console.log("Clinic updated successfully:", res.data);
      setClinic(res.data);
      setSuccess('Clinic information updated successfully');
      setLoading(false);
    } catch (err) {
      setError('Failed to update clinic information: ' + (err.response?.data.msg || err.message));
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="mb-4">Clinic Settings</h2>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Clinic Information</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Clinic Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter clinic name"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter clinic address"
                    required
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contact Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleInputChange}
                        placeholder="Enter contact number"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email (Optional)</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Opening Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="timings.open"
                        value={formData.timings.open}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Closing Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="timings.close"
                        value={formData.timings.close}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Working Days</Form.Label>
                  <div className="d-flex flex-wrap">
                    {workingDaysOptions.map(day => (
                      <Form.Check
                        type="checkbox"
                        id={`day-${day}`}
                        label={day}
                        key={day}
                        className="me-3 mb-2"
                        checked={formData.workingDays.includes(day)}
                        onChange={() => handleWorkingDaysChange(day)}
                      />
                    ))}
                  </div>
                </Form.Group>

                <hr className="my-4" />

                <h5 className="mb-3">Patient ID Settings</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Patient ID Prefix</Form.Label>
                      <Form.Control
                        type="text"
                        name="patientIdPrefix"
                        value={formData.patientIdPrefix}
                        onChange={handleInputChange}
                        placeholder="Enter prefix for patient IDs"
                      />
                      <Form.Text className="text-muted">
                        This will appear before the sequential number (e.g., "25-HWC-")
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Patient ID Start Number</Form.Label>
                      <div className="d-flex">
                        <Form.Control
                          type="number"
                          name="patientIdStart"
                          value={formData.patientIdStart}
                          onChange={(e) => setFormData({...formData, patientIdStart: parseInt(e.target.value) || 1})}
                          min="1"
                          placeholder="Enter starting number"
                          className="me-2"
                        />
                        <Button 
                          variant="danger" 
                          onClick={handleResetIdCounter}
                          disabled={loading}
                          title="Reset counter to 001"
                        >
                          Reset IDs
                        </Button>
                      </div>
                      <Form.Text className="text-muted">
                        The next new patient will start with this number (formatted as 001)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h5 className="mb-3">
                  <FaUserMd className="me-2" /> Select Doctors
                </h5>
                <p className="text-muted">Select up to 3 doctors to appear on reports</p>

                <div className="mb-3">
                  <Row>
                    {doctors.map(doctor => {
                      // Skip if doctor is null
                      if (!doctor) return null;
                      
                      const doctorIdStr = String(doctor._id || doctor.id);
                      const isSelected = selectedDoctors.some(id => String(id) === doctorIdStr);
                      const hasReachedLimit = selectedDoctors.length >= 3;
                      
                      console.log(`Rendering doctor ${doctor.name} with ID ${doctorIdStr}, selected: ${isSelected}`);
                      
                      return (
                        <Col md={4} key={doctorIdStr}>
                          <Form.Check
                            type="checkbox"
                            id={`doctor-${doctorIdStr}`}
                            label={`${doctor.name} (${doctor.specialization})`}
                            checked={isSelected}
                            onChange={() => handleDoctorSelection(doctorIdStr)}
                            disabled={!isSelected && hasReachedLimit}
                            className="mb-2"
                          />
                        </Col>
                      );
                    })}
                  </Row>
                </div>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" /> Save Settings
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Clinic Logo</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-3">
                {logoPreview ? (
                  <Image 
                    src={`${logoPreview}?t=${Date.now()}`} 
                    style={{ maxHeight: '150px' }} 
                    className="mb-3" 
                    fluid 
                  />
                ) : (
                  <div className="p-5 bg-light text-center mb-3">
                    <p className="mb-0">No logo uploaded</p>
                  </div>
                )}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Upload New Logo</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                />
                <Form.Text className="text-muted">
                  Recommended size: 300x150 pixels, Max size: 1MB
                </Form.Text>
              </Form.Group>

              <div className="d-grid">
                <Button
                  variant="success"
                  onClick={handleLogoUpload}
                  disabled={!logoFile || loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FaUpload className="me-2" /> Upload Logo
                    </>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ClinicSettings; 
